/**
 * Ad-scanner guard — keeps Mediavine from inserting a display ad in the gap
 * immediately above a first-party conversion block.
 *
 * WHY: Journey's wrapper does not read a slot list from us. It scans the
 * hydrated DOM and inserts between paragraph-level siblings. The 2026-08-02
 * live audit measured it choosing the boundary directly above our two best
 * capture units on 3 of 3 articles — an ad ending exactly 32px above the ZIP
 * reminder widget, and again 32px above the Buyer's Guide lead magnet. The ad
 * takes the attention the CTA needed, and the CTA then reads as more ad.
 *
 * Our own anti-stacking model (lib/blog-intent.ts) cannot help: it runs
 * server-side on the HTML string, while Mediavine injects client-side into the
 * hydrated DOM. Neither system can see the other.
 *
 * MECHANISM (verified 2026-08-02 against the live tag, not just their docs):
 * our served config at scripts.scriptwrapper.com/tags/d718fcea-*.js carries
 *   "content_selector": "article.relative .px-0 > .relative"
 *   "enable_divbuster": false
 * The selector resolves to BlogContentWithTOC's own root div, so the scanner
 * places units among that root's DIRECT CHILDREN, and with divbuster off it
 * does not descend into div children. Our .prose-enhanced segments are already
 * opaque to it for exactly this reason, and an .ad-cta-guard div will be too.
 * Wrapping only the CTA would not help — the boundary we care about is the one
 * *above* it — so we bind the CTA to the trailing block element before it.
 *
 * COST, stated honestly: this is NOT free. Because insertion points are the
 * root's child boundaries, and our prose is bundled into a handful of large
 * opaque segments, the page is boundary-starved — measured ad counts track
 * segment counts almost 1:1 (5 segments/5 ads, 8/8, 4/3 on three production
 * articles). Mediavine's own guidance says div-wrapping "can reduce the number
 * of ads that appear on your page." So budget roughly one lost impression per
 * guarded CTA rather than assuming the unit relocates. That trade — an ad
 * impression for a capture-form conversion — is a judgment call, which is why
 * this ships behind a flag and why the prod check measures TOTAL ad count, not
 * just whether the CTA is still an ad's next sibling.
 *
 * The deeper fix is the inverse of this file: give the scanner more legal
 * boundaries inside prose so it stops being forced onto component seams. See
 * obsidian/flags.md.
 */

/**
 * Block-level tags that Mediavine treats as paragraph-level siblings.
 *
 * blockquote is deliberately absent. A WYSIWYG blockquote always wraps a <p>,
 * so the inner <p> wins as the split point and the tail's orphaned
 * </blockquote> fails isSelfContained — it refused 100% of the time in a
 * 285-split corpus run, i.e. it was dead code. Leaving it out also avoids a
 * cosmetic regression: `.prose-enhanced > :first-child { margin-top: 0 }`
 * would strip a blockquote tail's 1.9rem top margin, which is the one shape
 * that would have broken the "visually inert" guarantee.
 */
const TRAILING_BLOCK_RE = new RegExp(
  [
    // Bare block elements.
    '<(?:p|ul|ol)\\b[^>]*>',
    // Prose wrapped in one of our own content-callout divs. Without this the
    // split is blind to any block inside a styled wrapper, which was 144 of
    // 147 refusals in a full 348-post corpus run — `extension-guidance` and
    // `fast-answer` alone are 98 of them, and lib/article-generator-seo.ts
    // emits both on essentially every generated article. Restricted to a
    // known allowlist rather than any <div>, so widget markers and affiliate
    // blocks keep their current behaviour instead of silently becoming
    // split points.
    '<div\\b[^>]*class="[^"]*\\b(?:extension-guidance|fast-answer|blog-content-image|blog-table-scroll-wrap)\\b[^"]*"[^>]*>',
  ].join('|'),
  'gi',
)

/** Sanity bound on the block we bind to a CTA. See splitTrailingBlock. */
const MAX_TAIL_CHARS = 4000

/**
 * Tools (server-marker widgets in blog HTML) whose gap-above must be protected.
 * Deliberately narrow — only the surfaces the audit actually measured stacking.
 * `reminder-widget` is the ZIP capture card.
 */
export const AD_GUARDED_TOOLS: ReadonlySet<string> = new Set(['reminder-widget'])

/**
 * True when every closing tag in `fragment` has a matching opening tag inside
 * it. Used to refuse a split that would tear a nested block (an affiliate
 * block's <div>, a table cell) in half and emit unbalanced HTML into
 * dangerouslySetInnerHTML.
 */
function isSelfContained(fragment: string): boolean {
  const closings = fragment.match(/<\/([a-z0-9]+)>/gi)
  if (!closings) return true
  const seen = new Set<string>()
  for (const raw of closings) {
    const name = raw.replace(/[</>]/g, '').toLowerCase()
    if (seen.has(name)) continue
    seen.add(name)
    const opens = (fragment.match(new RegExp(`<${name}\\b`, 'gi')) || []).length
    const closes = (fragment.match(new RegExp(`</${name}>`, 'gi')) || []).length
    if (closes > opens) return false
  }
  return true
}

/**
 * Split `html` just before its final top-level block element so the caller can
 * render that block inside the same wrapper as the CTA that follows it.
 *
 * Returns `tail: ''` (caller renders `html` unchanged) whenever splitting would
 * be unsafe or pointless: no block found, the content does not end on a closed
 * block, the tail is not self-contained, or the tail would swallow most of the
 * segment (which would suppress ads across a large stretch of prose rather than
 * at one boundary).
 */
export function splitTrailingBlock(html: string): { head: string; tail: string } {
  const NO_SPLIT = { head: html, tail: '' }
  if (!html) return NO_SPLIT

  const trimmed = html.trimEnd()
  // Must end on a closed block; anything else means we are mid-structure.
  // A trailing <hr>/<br> is tolerated and carried into the tail: many posts
  // close a section with a rule right before the CTA, and requiring a bare
  // </p> refused the split on those (4 of 49 in the 2026-08-02 local sweep).
  // Void elements have no closing tag, so they cannot unbalance the tail.
  // </div> is accepted because our content callouts close on one. It cannot
  // let an unbalanced tail through: isSelfContained still runs below, so a
  // tail carrying a stray </div> from a torn outer wrapper is refused.
  if (!/<\/(p|ul|ol|div)>(\s*<(hr|br)\s*\/?>)*$/i.test(trimmed)) return NO_SPLIT

  const candidates: number[] = []
  const re = new RegExp(TRAILING_BLOCK_RE.source, TRAILING_BLOCK_RE.flags)
  let m: RegExpExecArray | null
  while ((m = re.exec(html)) !== null) candidates.push(m.index)

  // Walk candidates from the innermost/latest outwards. The latest match is
  // often nested — a callout's inner <ul> sits after the wrapper <div> that
  // owns it, and slicing there would strand the wrapper's </div> in the tail.
  // Stepping outwards lands on the wrapper itself, which is the block the
  // reader actually sees as the last thing before the CTA.
  for (let i = candidates.length - 1; i >= 0; i--) {
    const start = candidates[i]
    if (start <= 0) break // nothing above it to keep the CTA company

    const tail = html.slice(start)
    if (!isSelfContained(tail)) continue
    // An HTML comment containing a block tag (`<p>b <!--<p>--> c</p>`) would
    // otherwise split inside the comment, leaving the head with an
    // unterminated `<!--` and the tail rendering a stray `-->` as text.
    if ((tail.match(/<!--/g) || []).length !== (tail.match(/-->/g) || []).length) continue
    // Sanity bound against a pathologically large blob; also stops the walk
    // outwards from binding an ever-larger share of the segment.
    if (tail.length > MAX_TAIL_CHARS) break

    return { head: html.slice(0, start), tail }
  }

  return NO_SPLIT
}
